<template>
  <div
    class="z-50 max-h-56 min-w-48 overflow-auto rounded-md border border-obsidian-200 bg-white p-1 shadow-lg"
  >
    <button
      v-for="(item, index) in items"
      :key="item.name"
      type="button"
      class="flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left text-sm transition-colors"
      :class="
        index === selectedIndex
          ? 'bg-hazel-100 text-obsidian-900'
          : 'text-obsidian-700 hover:bg-hazel-50'
      "
      @click="selectItem(index)"
      @mouseenter="selectedIndex = index"
    >
      <img
        v-if="item.fallbackImage"
        :src="item.fallbackImage"
        :alt="item.name"
        class="size-5 shrink-0 object-contain"
        draggable="false"
      />
      <span v-else class="w-6 shrink-0 text-center text-base leading-none">{{ item.emoji }}</span>
      <span class="truncate font-medium">:{{ item.name }}:</span>
    </button>
    <p v-if="!items.length" class="px-2 py-1.5 text-sm text-obsidian-500">
      {{ t('noMatches') }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { EmojiItem } from '@tiptap/extension-emoji';

const props = defineProps<{
  items: EmojiItem[];
  command: (item: { emoji: string }) => void;
}>();

const { t } = useI18n();
const selectedIndex = ref(0);

watch(
  () => props.items,
  () => {
    selectedIndex.value = 0;
  },
);

const selectItem = (index: number) => {
  const item = props.items[index];
  if (!item?.emoji) return;
  props.command({ emoji: item.emoji });
};

const onKeyDown = ({ event }: { event: KeyboardEvent }) => {
  if (event.key === 'ArrowUp') {
    selectedIndex.value =
      (selectedIndex.value + props.items.length - 1) % Math.max(props.items.length, 1);
    return true;
  }
  if (event.key === 'ArrowDown') {
    selectedIndex.value = (selectedIndex.value + 1) % Math.max(props.items.length, 1);
    return true;
  }
  if (event.key === 'Enter') {
    selectItem(selectedIndex.value);
    return true;
  }
  return false;
};

defineExpose({ onKeyDown });
</script>

<i18n lang="json">
{
  "en": {
    "noMatches": "No emoji found"
  },
  "de": {
    "noMatches": "Kein Emoji gefunden"
  },
  "es": {
    "noMatches": "No se encontró ningún emoji"
  }
}
</i18n>
